import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import config from "../Config/config";
import { checkDocsPermission, stripHtml } from "./infoShared";

const InfoList = () => {
  const navigate = useNavigate();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [syncingRawText, setSyncingRawText] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");

  const [permissionLoading, setPermissionLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  useEffect(() => {
    const init = async () => {
      const permission = await checkDocsPermission();
      setHasPermission(permission.allowed);
      setPermissionError(permission.error);
      setPermissionLoading(false);

      if (!permission.allowed) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${config.API_BASE_URL}/docs`);
        setDocs(response.data || []);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const filteredDocs = useMemo(() => {
    const lower = query.trim().toLowerCase();
    if (!lower) return docs;

    return docs.filter((doc) => {
      const titleMatch = (doc.title || "").toLowerCase().includes(lower);
      const textMatch = stripHtml(doc.description || "").toLowerCase().includes(lower);
      return titleMatch || textMatch;
    });
  }, [docs, query]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document and all of its sections?")) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/docs/${id}`);
      setDocs((prev) => prev.filter((doc) => doc.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleConfineDocsForAi = async () => {
    const confirmed = window.confirm(
      "Generate and save rawtext for all docs and sections now?"
    );
    if (!confirmed) return;

    setSyncingRawText(true);
    setSyncMessage("");

    try {
      const response = await axios.post(`${config.API_BASE_URL}/docs/confine-ai`);
      const { processedDocs = 0, processedSections = 0 } = response.data || {};
      setSyncMessage(
        `Done. Updated ${processedDocs} doc(s) and ${processedSections} section(s).`
      );
    } catch (err) {
      setSyncMessage(
        `Failed: ${err.response?.data?.error || err.message || "Unknown error"}`
      );
    } finally {
      setSyncingRawText(false);
    }
  };

  if (permissionLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="px-4 py-3 rounded-xl border border-slate-200 bg-white/90 text-slate-700 text-sm shadow-sm">
          Loading documentation...
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          {permissionError || "Access denied."}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 py-6 sm:py-8 bg-gradient-to-br from-slate-50 via-sky-50/70 to-slate-100">
      <div className="max-w-6xl mx-auto rounded-2xl border border-slate-200/90 bg-white/95 shadow-[0_18px_40px_rgba(15,23,42,0.10)] p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Documentation</h1>
            <p className="text-sm text-slate-600 mt-1">
              Manage internal guides and keep your docs structure clean.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search title or content"
              className="rounded-full border border-slate-300 px-4 py-2 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-500"
            />
            <button
              type="button"
              onClick={handleConfineDocsForAi}
              disabled={syncingRawText}
              className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                syncingRawText
                  ? "border-slate-300 bg-slate-100 text-slate-500 cursor-not-allowed"
                  : "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              }`}
            >
              {syncingRawText ? "Building Raw Text..." : "Confine Docs for AI"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/docs/create")}
              className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors"
            >
              New Document
            </button>
          </div>
        </div>

        {syncMessage && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm text-slate-700">
            {syncMessage}
          </div>
        )}

        <div className="mt-4 text-xs text-slate-500">
          Showing {filteredDocs.length} of {docs.length} document{docs.length === 1 ? "" : "s"}
        </div>

        {filteredDocs.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
            <p className="text-sm font-medium text-slate-700">
              {docs.length === 0 ? "No documents yet." : "No matching documents."}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {docs.length === 0
                ? "Create your first guide to get started."
                : "Try a different search term."}
            </p>
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {filteredDocs.map((doc) => {
              const preview = stripHtml(doc.description || "").slice(0, 170);
              return (
                <li
                  key={doc.id}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/docs/${doc.id}`}
                        className="text-lg font-semibold text-slate-900 hover:text-sky-700 transition-colors"
                      >
                        {doc.title || "Untitled Document"}
                      </Link>

                      {preview && (
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                          {preview}
                          {preview.length >= 170 ? "..." : ""}
                        </p>
                      )}

                      {doc.updatedAt && (
                        <p className="text-xs text-slate-500 mt-2">
                          Updated {new Date(doc.updatedAt).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/docs/${doc.id}/edit`)}
                        className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default InfoList;
